import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

let app = null;
let messaging = null;

/**
 * Initialize Firebase app
 */
const initializeFirebase = () => {
  if (app) return app;

  // Check if required config is present
  if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
    console.warn("Firebase config missing. Web push notifications disabled.");
    return null;
  }

  try {
    app = initializeApp(firebaseConfig);
    console.log("Firebase initialized successfully");

    // Send config to service worker
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.active?.postMessage({
          type: "FIREBASE_CONFIG",
          config: firebaseConfig,
        });
      });
    }

    return app;
  } catch (error) {
    console.error("Error initializing Firebase:", error);
    return null;
  }
};

/**
 * Get Firebase Messaging instance
 */
const getMessagingInstance = () => {
  if (messaging) return messaging;

  if (!app) {
    initializeFirebase();
  }

  if (!app) {
    return null;
  }

  try {
    messaging = getMessaging(app);
    return messaging;
  } catch (error) {
    console.error("Error getting Firebase Messaging:", error);
    return null;
  }
};

/**
 * Request notification permission and get FCM token
 * @returns {Promise<string|null>} FCM token or null if permission denied
 */
const requestNotificationPermission = async () => {
  try {
    // Check if notifications are supported
    if (!("Notification" in window)) {
      console.warn("This browser does not support notifications");
      return null;
    }

    // Check if service worker is supported
    if (!("serviceWorker" in navigator)) {
      console.warn("Service workers are not supported");
      return null;
    }

    // Request permission
    const permission = await Notification.requestPermission();
    console.log("Notification permission:", permission);

    if (permission !== "granted") {
      console.log("Notification permission denied");
      return null;
    }

    // Register service worker
    const registration = await navigator.serviceWorker.register(
      "/firebase-messaging-sw.js"
    );
    console.log("Service Worker registered:", registration);

    // Wait for service worker to be ready
    await navigator.serviceWorker.ready;

    // Send config to service worker
    registration.active?.postMessage({
      type: "FIREBASE_CONFIG",
      config: firebaseConfig,
    });

    // Get messaging instance
    const messagingInstance = getMessagingInstance();
    if (!messagingInstance) {
      console.error("Could not get messaging instance");
      return null;
    }

    // Get FCM token
    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
      console.warn("VAPID key not configured");
      return null;
    }

    const token = await getToken(messagingInstance, {
      vapidKey: vapidKey,
      serviceWorkerRegistration: registration,
    });

    if (token) {
      console.log("FCM Token obtained:", token.substring(0, 20) + "...");
      return token;
    } else {
      console.log("No FCM token available");
      return null;
    }
  } catch (error) {
    console.error("Error requesting notification permission:", error);
    return null;
  }
};

/**
 * Set up foreground message handler
 * @param {Function} callback - Callback function to handle incoming messages
 */
const onForegroundMessage = (callback) => {
  const messagingInstance = getMessagingInstance();
  if (!messagingInstance) {
    console.warn("Messaging not initialized");
    return null;
  }

  return onMessage(messagingInstance, (payload) => {
    console.log("Foreground message received:", payload);
    callback(payload);
  });
};

/**
 * Check if notifications are supported and enabled
 * @returns {boolean}
 */
const areNotificationsSupported = () => {
  return (
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
};

/**
 * Check current notification permission status
 * @returns {string} 'granted', 'denied', or 'default'
 */
const getNotificationPermissionStatus = () => {
  if (!("Notification" in window)) {
    return "unsupported";
  }
  return Notification.permission;
};

// Initialize Firebase on module load
initializeFirebase();

export {
  initializeFirebase,
  getMessagingInstance,
  requestNotificationPermission,
  onForegroundMessage,
  areNotificationsSupported,
  getNotificationPermissionStatus,
};
